import apiClient from '@/shared/api/client';
import type { ApiResponse } from '@/shared/api/types';
import type { EnrichmentData } from './uploadApi';

// ─── Types ───────────────────────────────────────────────────────────────────

export type StrategyStatus = 'not_started' | 'in_progress' | 'active' | 'archived';

export interface RefreshWarning {
  type: 'too_early' | 'due_for_refresh' | 'data_changed' | null;
  message: string | null;
  daysSinceCompletion: number | null;
}

export interface StrategyMeta {
  id: string;
  status: StrategyStatus;
  version: number;
  currentStep: number;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface StrategyVersion {
  version: number;
  status: string;
  createdAt: string;
  archivedAt: string | null;
}

export interface LifecycleData {
  /**
   * Latest non-archived version. Used by the wizard, which always operates on
   * a draft. May be the active version itself when no draft exists.
   */
  strategy: StrategyMeta | null;
  /**
   * Currently approved (status='active') version, if any. Distinct from
   * `strategy` when a draft refresh is in progress on top of a still-active
   * prior version. Dashboards rendering "the live strategy" should read this.
   */
  active: StrategyMeta | null;
  /** Convenience flag — equivalent to `active != null`. */
  hasActiveStrategy: boolean;
  refreshWarning: RefreshWarning;
  enrichment: EnrichmentData;
  versions: StrategyVersion[];
}

// ─── API ─────────────────────────────────────────────────────────────────────

export const lifecycleApi = {
  getLifecycle: (locationId: string) =>
    apiClient
      .get<ApiResponse<LifecycleData>>('/strategy/lifecycle', { params: { locationId } })
      .then((r) => r.data),

  startStrategy: (locationId: string, version?: number) =>
    apiClient
      .post<
        ApiResponse<unknown>
      >('/strategy/lifecycle/start', { locationId, ...(version !== undefined ? { version } : {}) })
      .then((r) => r.data),

  updateStep: (locationId: string, step: number, version?: number) =>
    apiClient
      .patch<
        ApiResponse<unknown>
      >('/strategy/lifecycle/step', { locationId, step, ...(version !== undefined ? { version } : {}) })
      .then((r) => r.data),

  completeStrategy: (
    locationId: string,
    approvedIndexes: number[],
    version?: number,
    insightsGeneratedAt?: string,
  ) =>
    apiClient
      .post<ApiResponse<unknown>>('/strategy/lifecycle/complete', {
        locationId,
        approvedIndexes,
        ...(version !== undefined ? { version } : {}),
        ...(insightsGeneratedAt ? { insightsGeneratedAt } : {}),
      })
      .then((r) => r.data),

  /** Open (or reuse) a refresh draft on top of the live strategy. */
  openRefresh: (locationId: string) =>
    apiClient
      .post<
        ApiResponse<{ version: number; created: boolean }>
      >('/strategy/lifecycle/refresh', { locationId })
      .then((r) => r.data),

  discardDraft: (locationId: string, version?: number) =>
    apiClient
      .post<ApiResponse<unknown>>('/strategy/lifecycle/discard-draft', {
        locationId,
        ...(version !== undefined ? { version } : {}),
      })
      .then((r) => r.data),
};
